import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { SortableComponent } from '@progress/kendo-angular-sortable';

@Component({
  selector: 'ups-reorder-rows-modal',
  templateUrl: 'reorder-rows-modal.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** T is the row type: `saved` hands back the same rows in their new order (string rows come back as strings). */
export class ReorderRowsModalComponent<T = unknown> {
  @ViewChild('sortableComponent') sortableComponent: SortableComponent;

  @Input() itemName = 'items';
  @Input() sortField = 'sortOrder';

  @Input() parentName: string;
  @Input() nestedPath: string;

  @Input() showArrows = true;

  @Input() doRegex = false;

  @Input() set reorderItems(reorderItems: T[]) {
    this._reorderItems = reorderItems;
    this.reorderDraggables.set([
      ...new Set<string>(
        reorderItems.map((itemDetail) =>
          this.resolvePath(itemDetail, this.nestedPath)
        )
      ),
    ]);
  }

  get reorderItems(): T[] {
    return this._reorderItems;
  }

  @Output() closed: EventEmitter<unknown> = new EventEmitter();
  @Output() saved = new EventEmitter<T[]>();

  // WI 31712: OnPush. The draggable labels are written only by the reorderItems input setter;
  // kendoSortableBinding reorders this same array in place, and closeReorder reads that order.
  readonly reorderDraggables = signal<string[]>([]);
  public _reorderItems: T[];

  constructor() {}

  closeReorder(save: boolean) {
    let isStringType = false;

    if (save) {
      for (const itemDetail of this.reorderItems) {
        if (typeof itemDetail !== 'string') {
          const sortOrder = this.reorderDraggables().indexOf(
            this.resolvePath(itemDetail, this.nestedPath)
          );
          const row = itemDetail as Record<string, unknown>;
          if (sortOrder !== row[this.sortField]) {
            row[this.sortField] = sortOrder;
          }
        } else {
          isStringType = true;
        }
      }

      if (isStringType) {
        // String rows: T is string here, so the reordered labels are the rows.
        this.reorderItems = [...this.reorderDraggables()] as T[];
      }

      this.saved.emit(this.reorderItems);
    }

    this.closed.emit();
  }

  moveTo(from, to) {
    this.sortableComponent.moveItem(from, to);
  }

  private resolvePath = (object, path, defaultValue?) => {
    if (path) {
      const orderName = path
        .split('.')
        .reduce((o, p) => (o ? o[p] : defaultValue), object);

      return this.doRegex ? orderName.replace(/^\d+-/, '') : orderName;
    } else {
      return object;
    }
  };
}
